import { useNavigate } from 'react-router-dom'
import {
  Settings,
  Briefcase,
  LayoutDashboard,
  LogOut,
  ChevronDown,
  ClipboardList,
  Globe,
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { UserChip, type UserChipUser } from '@/components/ui/user-chip'
import { authFeatureService } from '@/features/auth'
import { formatUserFacingError } from '@/lib/errors'
import { cn } from '@/lib/utils'

interface UserMenuProps {
  user: UserChipUser
  /** 'public' — public-facing nav (My Account, Service Requests, Settings, Admin Portal for admins)
   *  'admin'  — admin portal nav (View Site, Settings) */
  context?: 'public' | 'admin'
  isAdmin?: boolean
  size?: 'sm' | 'md' | 'lg'
  maxNameLength?: number
  className?: string
}

export function UserMenu({
  user,
  context = 'public',
  isAdmin = false,
  size = 'sm',
  maxNameLength = 20,
  className,
}: UserMenuProps) {
  const navigate = useNavigate()

  async function handleSignOut() {
    try {
      await authFeatureService.signOut()
      navigate('/')
    } catch (error) {
      alert(formatUserFacingError(error, 'We could not sign you out. Please try again.'))
    }
  }

  const displayName = user.displayName || user.email || 'Account'
  const email = user.email

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-1 rounded-full transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            className
          )}
          aria-label="Open user menu"
        >
          <UserChip user={user} size={size} maxNameLength={maxNameLength} />
          <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0 -ml-0.5" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        {/* Profile header */}
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2">
          <span className="font-semibold text-sm truncate">{displayName}</span>
          {email && (
            <span className="text-xs font-normal text-muted-foreground truncate">{email}</span>
          )}
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {context === 'public' && (
          <>
            <DropdownMenuItem onClick={() => navigate('/dashboard')}>
              <Briefcase className="mr-2 h-4 w-4" />
              My Account
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/request')}>
              <ClipboardList className="mr-2 h-4 w-4" />
              Service Requests
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
            {isAdmin && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/admin')}>
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  Admin Portal
                </DropdownMenuItem>
              </>
            )}
          </>
        )}

        {context === 'admin' && (
          <>
            <DropdownMenuItem onClick={() => window.open('/', '_blank')}>
              <Globe className="mr-2 h-4 w-4" />
              View Site
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate('/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Settings
            </DropdownMenuItem>
          </>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleSignOut}
          className="text-destructive focus:text-destructive"
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
